import React, { useState, useEffect } from "react";
import { useCart } from "../Cart/CartContext";
import DownloadPopup from "../Bots/DownloadPopup";

const API_BASE = process.env.REACT_APP_API_URL;

function MpesaPayment({ show, onClose, isCartPurchase = false }) {
  const {
    currentPurchase,
    cartItems,
    totalPrice,
    purchaseCart,
  } = useCart();

  const [phoneNumber, setPhoneNumber] = useState("");
  const [status, setStatus] = useState("");
  const [loading, setLoading] = useState(false);
  const [showDownload, setShowDownload] = useState(false);
  const [downloadCartFiles, setDownloadCartFiles] = useState([]);

  useEffect(() => {
    setPhoneNumber("");
    setStatus("");
    setShowDownload(false);
    setDownloadCartFiles([]);
    setLoading(false);
  }, [show]);

  if (!show || (!isCartPurchase && !currentPurchase)) return null;

  const formatPhoneNumber = (rawPhone) => {
    let phone = rawPhone.trim().replace(/\s+/g, "");
    if (phone.startsWith("07") || phone.startsWith("01")) return "254" + phone.slice(1);
    if (phone.startsWith("+254")) return phone.replace("+", "");
    if (phone.startsWith("254") && phone.length === 12) return phone;
    return "";
  };

  const handleMpesaPayment = async () => {
    if (!phoneNumber.trim()) {
      setStatus("⚠️ Please enter your M-Pesa number.");
      return;
    }

    const formattedPhone = formatPhoneNumber(phoneNumber);
    if (!/^2547\d{8}$|^2541\d{8}$/.test(formattedPhone)) {
      setStatus("⚠️ Invalid phone number. Use 07/01 or full 2547/2541 format.");
      return;
    }

    const amount = isCartPurchase ? totalPrice : currentPurchase.price;
    const itemList = isCartPurchase
      ? cartItems.map((i) => `${i.heading} × ${i.quantity}`).join(", ")
      : currentPurchase.heading;

    setStatus(`🔄 Sending STK Push of Ksh ${amount} to ${formattedPhone}...`);
    setLoading(true);

    try {
      const res = await fetch(`${API_BASE}/api/mpesa/stkpush`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ phoneNumber: formattedPhone, amount, itemDescription: itemList }),
      });

      const data = await res.json();
      const checkoutRequestID = data.checkoutRequestID;

      if (data.success && checkoutRequestID) {
        setStatus("📲 STK Push sent. Waiting for confirmation...");

        let attempts = 0;
        const poll = setInterval(async () => {
          attempts++;
          const statusRes = await fetch(`${API_BASE}/api/mpesa/check-status`, {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({ checkoutRequestID }),
          });

          const statusData = await statusRes.json();

          if (statusData.success) {
            clearInterval(poll);
            setStatus("✅ Payment confirmed! Download ready.");
            setLoading(false);

            if (isCartPurchase) {
              const preservedFiles = cartItems.map((item) =>
                item.fileName || `${item.heading.replace(/\s+/g, "_").toLowerCase()}.xml`
              );
              setDownloadCartFiles(preservedFiles);
              purchaseCart();
            }

            setShowDownload(true);
          } else if (!statusData.success && attempts >= 10) {
            clearInterval(poll);
            setStatus("❌ Payment not completed. Please try again.");
            setLoading(false);
          }
        }, 3000);
      } else {
        setStatus(`❌ Failed: ${data.message || data.error || "Unknown error."}`);
        setLoading(false);
      }
    } catch (err) {
      console.error("STK Error:", err);
      setStatus("❌ Server error. Please check backend or internet.");
      setLoading(false);
    }
  };

  const singleFileName = currentPurchase?.fileName ||
    (currentPurchase?.heading
      ? `${currentPurchase.heading.replace(/\s+/g, "_").toLowerCase()}.xml`
      : "bot.xml");

  const singleFileUrl = currentPurchase?.fileUrl ||
    (currentPurchase?.heading
      ? `/downloads/${currentPurchase.heading.replace(/\s+/g, "_").toLowerCase()}.xml`
      : "/downloads/bot.xml");

  return (
    <>
      <div className="fixed inset-0 bg-black bg-opacity-60 flex items-center justify-center z-50">
        <div className="bg-white text-black p-6 rounded-lg w-80 shadow-2xl relative">
          <button
            onClick={onClose}
            className="absolute top-2 right-2 text-red-600 font-bold text-xl"
          >
            ×
          </button>

          <h2 className="text-lg font-bold text-center mb-3">
            {isCartPurchase ? "Pay for All Bots" : "Buy via M-Pesa"}
          </h2>

          <div className="text-sm mb-3">
            {isCartPurchase ? (
              <>
                <p><strong>Total Bots:</strong> {cartItems.length}</p>
                <p><strong>Total Amount:</strong> Ksh {totalPrice}</p>
              </>
            ) : (
              <>
                <p><strong>Bot:</strong> {currentPurchase?.heading}</p>
                <p><strong>Amount:</strong> Ksh {currentPurchase?.price}</p>
              </>
            )}
          </div>

          <input
            type="text"
            placeholder="Enter M-Pesa number"
            value={phoneNumber}
            onChange={(e) => setPhoneNumber(e.target.value)}
            className="w-full p-2 border rounded mb-3"
            disabled={loading}
          />

          <button
            onClick={handleMpesaPayment}
            className="w-full bg-green-600 hover:bg-green-500 text-white py-2 rounded disabled:opacity-60"
            disabled={loading}
          >
            {loading ? "Processing..." : "Pay Now"}
          </button>

          {status && (
            <p className="mt-3 text-sm text-center font-medium text-gray-700">{status}</p>
          )}
        </div>
      </div>

      <DownloadPopup
        show={showDownload}
        onClose={() => {
          setShowDownload(false);
          onClose();
        }}
        isCart={isCartPurchase}
        fileName={singleFileName}
        fileUrl={singleFileUrl}
        cartFileNames={downloadCartFiles}
      />
    </>
  );
}

export default MpesaPayment;
